import { motion, useReducedMotion } from "framer-motion";
import type { MotionValue } from "framer-motion";
import { EASE_INTRO } from "../../../lib/motion";
import { CalmReveal } from "../../../shared/motion/calm-reveal";
import { GlassPanel } from "../../../shared/surfaces/glass-panel";
import type { TChatStatus, TSectionTwoIntro } from "../../three/use-section-three-scroll";
import { chatBoxMinHeightPx } from "./chat-box-height";
import { ChatMessage } from "./chat-message";
import {
	SECTION_TWO_CHAT_MESSAGES,
	SECTION_TWO_SUBTITLE,
	SECTION_TWO_TITLE,
	SECTION_TWO_TITLE_BLOCK_ID,
	SECTION_TWO_TITLE_ID,
} from "./chat-transcript";
import { groupChatTurns } from "./group-chat-turns";
import { useChatFit } from "./use-chat-fit";
import { useChatPlayback } from "./use-chat-playback";

/** Turns fade in. Height motion lives on each message so the card is never clipped. */
const TURN_ENTER = { opacity: 0 };
const TURN_IDLE = { opacity: 1 };
const TURN_TRANSITION = { duration: 0.4, ease: EASE_INTRO } as const;

/* Title + subtitle mirror the section-one entrance: words rise out of a clipped line
 with a blur settle, staggered; the subtitle follows as one block. */
const TITLE_WORDS = SECTION_TWO_TITLE.split(" ");
const TITLE_ENTER_S = 0.9;
const TITLE_WORD_STAGGER_S = 0.12;
const SUBTITLE_DELAY_S = 0.45;
const TITLE_WORD_HIDDEN = { opacity: 0, filter: "blur(12px)", y: "100%", scaleY: 1.35, originY: 1 };
const TITLE_WORD_SHOWN = { opacity: 1, filter: "blur(0px)", y: 0, scaleY: 1, originY: 1 };
const SUBTITLE_HIDDEN = { opacity: 0, y: 16, filter: "blur(10px)" };
const SUBTITLE_SHOWN = { opacity: 1, y: 0, filter: "blur(0px)" };

type TChatGlassProps = {
	y: MotionValue<number>;
	scale: MotionValue<number>;
	status: TChatStatus;
	intro: TSectionTwoIntro;
};

export function ChatGlass({ y, scale, status, intro }: TChatGlassProps) {
	const reducedMotion = useReducedMotion();
	const playing = status === "playing" && !reducedMotion;
	const visible = status !== "idle";
	const revealAll = status === "complete" || Boolean(reducedMotion);
	const showTitle = intro !== "idle";
	const showChat = intro === "chat-playing";
	/* Scrub mode: the chat already played through once, so reverse scroll should snap
	 everything visible instead of re-running the staggered title entrance. */
	const scrub = status === "complete";

	const { visibleMessages, typingMessage } = useChatPlayback({
		messages: SECTION_TWO_CHAT_MESSAGES,
		active: playing,
		revealAll,
	});

	/* The typing message is the next pending one — appending it keeps the turn (and its
	 avatar) mounted while the body swaps from the typing label to the response. */
	const turns = groupChatTurns(typingMessage ? [...visibleMessages, typingMessage] : visibleMessages);

	/* The full transcript is taller than short viewports — measure it from a hidden
	 static clone and scale the whole unit (title + card) so the demo never clips. */
	const { fitScale, fitTop, cardHeight, cardMeasureRef, titleMeasureRef } = useChatFit();
	const chatMinHeight = chatBoxMinHeightPx(cardHeight);
	const fullTranscriptTurns = groupChatTurns(SECTION_TWO_CHAT_MESSAGES);

	return (
		<motion.div
			className="absolute z-3 top-[34%] left-1/2 w-[min(672px,calc(100%-32px))] max-lg:w-[min(672px,calc(100%-72px))]"
			style={{ x: "-50%", y, scale, top: fitTop }}
		>
			<div aria-hidden="true" ref={cardMeasureRef} className="pointer-events-none invisible absolute inset-x-0 top-0">
				<GlassPanel>
					<div className="flex flex-col gap-6">
						{fullTranscriptTurns.map((turn) => (
							<div
								key={`measure-${turn.role}-${turn.messages[0]?.id ?? "turn"}`}
								className={turn.role === "user" ? "self-end" : "self-stretch"}
							>
								<ChatMessage role={turn.role} messages={turn.messages} animateText={false} />
							</div>
						))}
					</div>
				</GlassPanel>
			</div>

			<div
				className="relative"
				style={fitScale === 1 ? undefined : { transform: `scale(${fitScale})`, transformOrigin: "50% 0" }}
			>
				<div
					id={SECTION_TWO_TITLE_BLOCK_ID}
					ref={titleMeasureRef}
					className="absolute bottom-full left-0 right-0 flex flex-col items-center pb-[clamp(24px,3.5vh,40px)] text-center"
				>
					<h2
						id={SECTION_TWO_TITLE_ID}
						className="m-0 flex flex-wrap items-baseline justify-center gap-x-[0.28em] text-[clamp(24px,calc(0.95vw+20.3px),34px)] font-normal leading-[1.15] tracking-[-0.03em] text-teal-500"
					>
						{TITLE_WORDS.map((word, index) => (
							<span key={`${index}-${word}`} className="inline-block overflow-hidden pb-[0.06em]">
								<motion.span
									className="inline-block"
									initial={reducedMotion ? false : TITLE_WORD_HIDDEN}
									animate={showTitle ? TITLE_WORD_SHOWN : TITLE_WORD_HIDDEN}
									transition={
										reducedMotion || scrub
											? { duration: 0 }
											: {
													duration: TITLE_ENTER_S,
													delay: index * TITLE_WORD_STAGGER_S,
													ease: EASE_INTRO,
													filter: {
														duration: TITLE_ENTER_S * 0.3,
														delay: index * TITLE_WORD_STAGGER_S,
														ease: EASE_INTRO,
													},
													opacity: {
														duration: TITLE_ENTER_S * 0.8,
														delay: index * TITLE_WORD_STAGGER_S,
														ease: EASE_INTRO,
													},
												}
									}
								>
									{word}
								</motion.span>
							</span>
						))}
					</h2>
					<motion.p
						className="m-0 mt-[18px] w-[min(600px,100%)] max-w-[400px] max-md:mt-3 text-center text-landing-body font-normal text-teal-500 text-base pb-[0.06em]"
						initial={reducedMotion ? false : SUBTITLE_HIDDEN}
						animate={showTitle ? SUBTITLE_SHOWN : SUBTITLE_HIDDEN}
						transition={
							reducedMotion || scrub
								? { duration: 0 }
								: {
										duration: TITLE_ENTER_S,
										delay: SUBTITLE_DELAY_S,
										ease: EASE_INTRO,
										filter: { duration: TITLE_ENTER_S * 0.3, delay: SUBTITLE_DELAY_S, ease: EASE_INTRO },
									}
						}
					>
						{SECTION_TWO_SUBTITLE}
					</motion.p>
				</div>

				<motion.div
					initial={false}
					animate={showChat ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
					transition={reducedMotion || status === "complete" ? { duration: 0 } : { duration: 0.45, ease: EASE_INTRO }}
				>
					<GlassPanel style={chatMinHeight === undefined ? undefined : { minHeight: chatMinHeight }}>
						<CalmReveal
							visible={visible}
							playing={playing}
							hidden={{ opacity: 0, y: 24 }}
							shown={{ opacity: 1, y: 0 }}
							className="flex flex-col gap-6"
						>
							{turns.map((turn) => (
								<motion.div
									key={`${turn.role}-${turn.messages[0]?.id ?? "turn"}`}
									initial={playing ? TURN_ENTER : false}
									animate={TURN_IDLE}
									transition={TURN_TRANSITION}
									className={turn.role === "user" ? "self-end" : "self-stretch"}
								>
									<ChatMessage
										role={turn.role}
										messages={turn.messages}
										animateText={playing}
										typingMessageId={typingMessage?.id}
									/>
								</motion.div>
							))}
						</CalmReveal>
					</GlassPanel>
				</motion.div>
			</div>
		</motion.div>
	);
}
